import { slugCiudad } from "./model";
import { areaM2 } from "./pricing";
import type { Ciudad, Cotizacion, Pieza, Vendedor } from "./types";

const redondear = (n: number) => Math.round(n * 1000) / 1000;

// Hoja de orden a producción: lo que planta necesita para fabricar e instalar
// (qué, cuánto, medidas, materiales, dónde). El navegador no manda precios,
// márgenes ni descuentos: el único valor comercial de la hoja (valor aprobado)
// lo pone el backend desde el total que n8n validó al guardar. Sale de la
// versión guardada (número oficial), así que el botón exige que no haya
// cambios sin guardar.

// Lo que la cotización no trae y el vendedor llena en el formulario al
// descargar (secciones 1, 5, 6 y 7 del formato de Diez Visual). Fechas
// AAAA-MM-DD; todo opcional.
export const ENTREGABLES = [
  ["diseno_2d", "Diseño 2D"],
  ["render_3d", "Render 3D"],
  ["plano_tecnico", "Plano técnico"],
  ["arte_final", "Arte final"],
  ["despiece", "Despiece para producción"],
] as const;
export type Entregable = (typeof ENTREGABLES)[number][0];
export interface DatosProduccion {
  fecha_entrega_comprometida: string;
  fecha_presentacion_diseno: string;
  fecha_implementacion: string;
  entregables: Entregable[];
  arte_final_url: string;
  plano_tecnico_url: string;
  despiece_url: string;
  levantamiento_url: string;
  observaciones: string;
}
export const datosVacios = (): DatosProduccion => ({
  fecha_entrega_comprometida: "",
  fecha_presentacion_diseno: "",
  fecha_implementacion: "",
  entregables: [],
  arte_final_url: "",
  plano_tecnico_url: "",
  despiece_url: "",
  levantamiento_url: "",
  observaciones: "",
});
// Lo que devuelve el modo consulta del workflow (orden ya registrada).
export interface OrdenRegistrada
  extends Partial<Record<keyof DatosProduccion, unknown>> {
  numero_orden: string;
  estado: "pendiente" | "en_produccion" | "instalado";
}
export function datosDeOrden(o: OrdenRegistrada | null): DatosProduccion {
  const d = datosVacios();
  if (!o) return d;
  const textoDe = (v: unknown) => (typeof v === "string" ? v : "");
  return {
    fecha_entrega_comprometida: textoDe(o.fecha_entrega_comprometida),
    fecha_presentacion_diseno: textoDe(o.fecha_presentacion_diseno),
    fecha_implementacion: textoDe(o.fecha_implementacion),
    entregables: Array.isArray(o.entregables)
      ? ENTREGABLES.map(([k]) => k).filter((k) =>
          (o.entregables as unknown[]).includes(k),
        )
      : [],
    arte_final_url: textoDe(o.arte_final_url),
    plano_tecnico_url: textoDe(o.plano_tecnico_url),
    despiece_url: textoDe(o.despiece_url),
    levantamiento_url: textoDe(o.levantamiento_url),
    observaciones: textoDe(o.observaciones),
  };
}
function medidas(p: Pieza) {
  const lineal = ["ml", "cm_lineal"].includes(p.unidad);
  const directo = !lineal && p.areaM2 != null;
  if (
    p.unidad === "unidad" &&
    !(p.anchoCm > 0 || p.altoCm > 0 || p.profundidadCm > 0)
  )
    return null;
  return {
    ancho_cm: directo ? null : p.anchoCm || null,
    alto_cm: lineal || directo ? null : p.altoCm || null,
    profundidad_cm: p.profundidadCm || null,
    area_m2: directo ? p.areaM2 || null : null,
    lineal,
    unidad_medida: p.unidadMedida ?? "cm",
  };
}

// El backend registra la orden en `ordenes_produccion` (una por cotización
// guardada, identificada por registroId) y devuelve el número OP-… que se
// imprime; `orden` es el snapshot que queda guardado con ella.
// `ciudades` (del catálogo) traduce el slug que guarda el sitio ("quitolocal")
// al nombre que ve planta, igual que el selector del paso 2.
export function construirOrdenProduccion(
  c: Cotizacion,
  v: Vendedor,
  ciudades: Ciudad[] = [],
  datos: DatosProduccion = datosVacios(),
) {
  if (!c.numeroOficial || !c.registroId)
    throw new Error(
      "Guarda la cotización para generar la orden de producción.",
    );
  return {
    vendedor_id: v.id,
    cotizacion_id: c.registroId,
    orden: construirHoja(c, v, ciudades),
    datos_produccion: datos,
  };
}
function construirHoja(c: Cotizacion, v: Vendedor, ciudades: Ciudad[]) {
  return {
    numero_cotizacion: c.numeroOficial,
    etiqueta_propuesta: c.etiquetaPropuesta ?? null,
    cliente: {
      nombre: c.cliente.nombre,
      encargado: c.cliente.encargado,
      nombre_proyecto: c.cliente.nombre_proyecto,
    },
    contacto: c.contacto
      ? {
          nombre: c.contacto.nombre,
          telefono: c.contacto.telefono || c.telefonoEnvio || null,
        }
      : null,
    tiempo_entrega: c.condiciones.tiempo_entrega || null,
    // El backend lo reemplaza por el dueño de la cotización; este es solo el
    // respaldo si no lo encuentra.
    vendedor_nombre: v.nombre,
    foto_fachada_url: c.fachadaUrl || null,
    simulacion_ia_url: c.simulacionUrl || null,
    sitios: c.sitios.map((s) => ({
      nombre_sitio: s.nombre,
      direccion_instalacion: s.direccion,
      ciudad:
        ciudades.find((x) => slugCiudad(x.ciudad) === s.ciudad)?.ciudad ??
        (s.ciudadPersonalizada?.ciudad || s.ciudad),
      sede_atiende: s.sede || null,
      // Add-ons (instalación, andamios…) y visitas son trabajo de campo: se
      // listan por nombre y cantidades, sin su costo.
      addons: s.addons.map((a) => ({
        nombre: a.nombre,
        cantidades: a.cantidades,
      })),
      visitas: s.logistica.incluida ? s.logistica.visitas : null,
      productos: s.productos.map((pr) => ({
        nombre: pr.nombre || "(sin nombre)",
        imagenes: pr.imagenes,
        piezas: pr.piezas.map((p) => ({
          familia: p.familia,
          variante: p.variante,
          material: p.material,
          iluminacion: p.iluminacion,
          cantidad: p.cantidad,
          unidad: p.unidad,
          medidas: medidas(p),
          codigo_articulo: p.catalogoEspecial?.codigoArticulo ?? null,
          // Consumo total para las `cantidad` piezas (los materiales por área
          // vienen por m²; mismo criterio que costoMateriales en pricing.ts).
          materiales: (p.materiales ?? []).map((m) => ({
            nombre: m.nombre,
            unidad: m.unidad,
            cantidad_total: redondear(
              m.cantidad * (m.porArea ? areaM2(p) : 1) * p.cantidad,
            ),
          })),
          materiales_referencia: p.materialesReferencia ?? [],
        })),
      })),
    })),
  };
}
